import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { FilterMetadata, MenuItem, MessageService } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RatingModule } from 'primeng/rating';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';

import { firstValueFrom } from 'rxjs';
import { SETTINGS, Settings } from '../../../app.settings';
import { ProcessingComponent } from '../../../components';


import { FilterMatchMode, FilterService, SelectItem, TreeNode } from 'primeng/api';

import { IAppUser, ISearchRequest, ISearchSort, ISearchRequestFilter, ISearchFilterValue, MetricsService, ISearchResult } from '../../../services/insecta';

import { afterRender } from '../../../common/after-render';
import { notifyAndTrackError } from '../../../common/error';
import { getItemDescriptionColumn, isNotEmpty, isStringEmpty, isStringEqual, toShortDate } from '../../../common/functions';
import { AppInitService } from '../../../services/app-init.service';
import { AppInsightsService } from '../../../services/app-insights.service';

@Component({
  selector: 'app-metrics',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CheckboxModule,
    FileUploadModule,
    FloatLabelModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    TableModule,
    TextareaModule,
    TooltipModule,
    TreeSelectModule,
    RouterModule,
    ProcessingComponent,
    PaginatorModule,
    RadioButtonModule,
    RatingModule,
    InputNumberModule,
    DialogModule,
    Breadcrumb
  ],

  templateUrl: './metrics.component.html'
})
export class MetricsComponent implements OnInit {
  @Input() appUser!: IAppUser;
  @ViewChild('dt') dt!: Table;

  userSettingsJson: { [key: string]: any } = {};
  route: any;
  filtersActive: boolean = false;
  cols: any[] = [];
  activeColumnFilters: Set<string> = new Set<string>();
  selectedOrderItemInstances: any[] = [];


  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Admin:Metrics`,
    breadcrumbItems: <MenuItem[]>[]
  };

  customMatchModeOptions: SelectItem[] = [
      { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
      { label: 'Contains', value: FilterMatchMode.CONTAINS },
      { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
      { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
      { label: 'Equals', value: FilterMatchMode.EQUALS },
      { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
      { label: 'Is Empty', value: 'isEmpty' },
      { label: 'Has Value', value: 'isNotEmpty' }
    ];
    dateMatchModeOptions: SelectItem[] = [
      { label: 'Date Is', value: FilterMatchMode.DATE_IS },
      { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
      { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
      { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
      { label: 'Is Empty', value: 'isEmpty' },
      { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
      { label: 'Equals', value: FilterMatchMode.EQUALS },
      { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
      { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
      { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
      { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
      { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
      { label: 'Is Empty', value: 'isEmpty' },
      { label: 'Has Value', value: 'isNotEmpty' }
];
  exportColumns: string[] = [
    'Site',
    'SiteCode',
    'DeliveryLocationName',
    'DeliveryLocationType',
    'Requester',
    'RequesterName',
    'OrderName',
    'GrowthStage',
    'OrderItemTypeName',
    'ProjectName',
    'OrderItemInstanceSequence',
    'Note',
    'Insect',
    'InsectIsExternal',
    'InsectSetupDate',
    'ContainerName',
    'InsectQuantity',
    'StandardInfestationRate',
    'InfestationRateQuantity',
    'SubstrateAmount',
    'SubstrateName',
    'SubstrateSetupDate',
    'SubstrateIsExternal',
    'InstanceHasExternal',
    'StandardFillAmount',
    'SubstrateQuantity',
    'DeliveryDate',
    'OrderItemInstanceStatus',
    'OrderItemInstanceQuantity',
    'PermitName',
    'SitePermitsName',
    'VendorName',
    'VendorDeliveryDate',
    'VendorDelay',
    'VendorDelayNote',
    'ShippingDate',
    'ShippingVendor',
    'ShippingTrackingNumber',
    'ActualDeliveryLocation',
    'EntityType',
    'EntityId',
    '[How was the overall quality of your order?]',
    '[Comments]',
    '[The number of insects delivered was:]',
    '[The quality of insects delivered was:]',
    '[The insect stage (instar) delivered was:]',
    '[The packages (plastic pots/bags) were delivered:]'
  ];
  tableColumns: string[] = [
    'OrderItemInstanceSequence',
    'DeliveryDate',
    'OrderItemTypeName',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'OrderItemInstanceQuantity',
    'VendorName',
    'SitePermitsName',
    'PermitName',
    'Note',
    'RequesterName',
    'ProjectName'
  ];

  instances: any[] = [];

  skip: number = 0;
  take: number = 15;
  totalCount: number = 0;

  metricsService: MetricsService = inject(MetricsService);
  searchRequest: ISearchRequest = {
    columns: [],
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  itemDescName: string = '';
  siteName: string = '';
  

  constructor(
    @Inject(SETTINGS) private settings: Settings,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private msgService: MessageService,
    private metricService: MetricsService
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Metrics`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/metrics`);
  }

  ngOnInit(): void {
    this.pageVars.isReady = false;
    this.pageVars.breadcrumbItems = [{ label: 'Metrics' }];

    this.appUser = this.appInit.appUser;
    if (this.appUser) {
      this.userSettingsJson =
        !isStringEmpty(this.appUser.userSettingsJson) || isStringEqual(this.appUser.userSettingsJson, '{}')
          ? JSON.parse(this.appUser.userSettingsJson)
          : {};

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteName = this.userSettingsJson['Default Site'] || '';
    }
    this.initializeColumns();
    afterRender(() => this.loadInitialData());
  }

  initializeColumns() {
    const descField = this.itemDescName || 'ItemDescription';
    this.cols = [
      { field: 'OrderItemInstanceSequence', header: 'Order Item Instance Sequence', type: 'text', template: 'link' },
      { field: 'DeliveryDate', header: 'Delivery Date', type: 'date' },
      { field: 'OrderItemTypeName', header: 'Item Type', type: 'text' },
      { field: descField, header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'OrderItemInstanceQuantity', header: 'Quantity', type: 'numeric' },
      { field: 'VendorName', header: 'Vendor', type: 'text' },
      { field: 'SitePermitsName', header: 'Site Permit', template: 'tooltip', type: 'text', style: { 'min-width': '250px' } },
      { field: 'PermitName', header: 'Requester Permit', template: 'tooltip', type: 'text', style: { 'min-width': '250px' } },
      { field: 'Note', header: 'Note', template: 'tooltip', type: 'text', style: { 'min-width': '250px' } },
      { field: 'RequesterName', header: 'Requester', type: 'text' },
      { field: 'ProjectName', header: 'Cost Center(s)', template: 'tooltip', type: 'text', style: { 'min-width': '250px' } }
    ];
  }

  loadInitialData() {
    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'Site',
      logic: 'and',
      values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
    });

    this.searchRequest.sorts = [];
    this.searchRequest.sorts.push(<ISearchSort>{ name: 'OrderItemInstanceSequence', direction: 'ASC' });

    const stateKey = 'metrics-table';
    try {
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);

        if (state.filters) {
          this.tableColumns.forEach((columnName) => {
            const columnFilters = state.filters[columnName] as FilterMetadata[];
            if (columnFilters && columnFilters.length > 0) {
              const tableFilter = <ISearchRequestFilter>{
                fieldname: columnName,
                logic: columnFilters[0].operator,
                values: []
              };
              columnFilters.forEach((filterValue: FilterMetadata) => {
                if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
                  tableFilter.values.push(<ISearchFilterValue>{
                    operator: filterValue.matchMode.toUpperCase(),
                    value: ''
                  });
                } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
                  tableFilter.values.push(<ISearchFilterValue>{
                    operator: filterValue.matchMode || 'contains',
                    value: filterValue.matchMode?.startsWith('date') ? toShortDate(filterValue.value) : `${filterValue.value}`
                  });
                }
              });

              if (tableFilter.values.length > 0) {
                this.searchRequest.filters.push(tableFilter);
                this.activeColumnFilters.add(columnName);
              }
            }
          });
        }

        if (state.sortField) {
          this.searchRequest.sorts = [];
          this.searchRequest.sorts.push(<ISearchSort>{
            name: state.sortField,
            direction: state.sortOrder === 1 ? 'ASC' : 'DESC'
          });
        }

        if (state.first !== undefined) {
          this.searchRequest.skip = state.first;
        }
        if (state.rows !== undefined) {
          this.searchRequest.take = state.rows;
        }

        this.filtersActive = this.activeColumnFilters.size > 0 || !!state.sortField;
      }
    } catch (e) {
      console.error('Error reading stored table state in loadInitialData', e);
    }

    this.executeSearch();
  }

  executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.tableColumns, this.itemDescName];

    firstValueFrom(this.metricService.getInstanceMetrics(request))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.instances = JSON.parse(response?.results ?? `[]`);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'ExecuteSearch:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  handleTableEvents(event: TableLazyLoadEvent) {
    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();

    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'Site',
      logic: 'and',
      values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
    });

    this.tableColumns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });

        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.searchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{ name: 'OrderItemInstanceSequence', direction: 'ASC' });
    }

    this.searchRequest.skip = event.first;
    this.searchRequest.take = event.rows ?? this.take;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);
    this.executeSearch();
  }

  async export() {
    this.pageVars.isReady = false;

    try {
      const exportRequest = this.searchRequest;
      exportRequest.columns = [...this.exportColumns, this.itemDescName];
      exportRequest.skip = 0;
      exportRequest.take = this.totalCount;

      await this.metricService.getInstanceMetricsXlsx(exportRequest);
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, {
        component: this.pageVars.pageId,
        action: 'Export:Error'
      });
    } finally {
      this.pageVars.isReady = true;
    }
  }
  clearFilters(table: Table) {
    table.sortField = null;
    table.sortOrder = 1;
    table.clear();
    if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
    this.activeColumnFilters.clear();
    this.filtersActive = false;

    try {
      const stateKey = 'metrics-table';
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);
        delete state.sortField;
        delete state.sortOrder;
        delete state.filters;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage in clearFilters', e);
    }

    setTimeout(() => {
      this.searchRequest.filters = [];
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'Site',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
      });
      this.searchRequest.sorts = [{ name: 'OrderItemInstanceSequence', direction: 'ASC' }];
      this.searchRequest.skip = 0;
      this.searchRequest.take = this.take;
      this.executeSearch();
    }, 100);
  }
  
  resetPage(table: Table) {
      table.clear();
      table.clearState();
      window.location.reload();
    }

    
  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }
}

